/**
 * Asset URL helpers for CDN delivery.
 * Resolves asset paths to Cloudflare R2 CDN URLs in production/staging,
 * or local /public paths in development.
 *
 * Usage:
 *   import { productAsset, staticAsset } from '@/lib/asset-urls';
 *
 *   // Extensionless — MultiFormatImage appends .webp / .jpg automatically
 *   <MultiFormatImage src={productAsset('product/trench-shield/trench-shield')} ... />
 *
 *   // With extension (PDFs, SVGs, PNGs that shouldn't have format fallback)
 *   <img src={productAsset('product/trench-shield/trench-shield-annotate.webp')} />
 */

function resolveR2Base() {
    try {
        if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_R2_URL) {
            return String(process.env.NEXT_PUBLIC_R2_URL).replace(/\/$/, '');
        }
    } catch (_e) {
        // ignore
    }
    return '';
}

/**
 * Resolve a path to a CDN URL (production/staging) or local path (development).
 * @param {string} path - Asset path, with or without leading slash
 * @returns {string}
 */
function resolveAsset(path) {
    if (!path) return '';
    // Absolute URLs (e.g. already-resolved R2 URLs) pass through unchanged
    if (/^https?:\/\//i.test(path)) return path;
    const normalised = path.replace(/^\/+/, '');
    const r2Base = resolveR2Base();
    if (r2Base) return `${r2Base}/${normalised}`;
    return `/${normalised}`;
}

/**
 * Get the CDN URL for a product image or asset.
 * Pass an extensionless path when using with MultiFormatImage.
 *
 * @param {string} path - e.g. 'product/trench-shield/trench-shield'
 * @returns {string} Full CDN URL in production; local path in development
 */
function productAsset(path) {
    return resolveAsset(path);
}

/**
 * Get the CDN URL for a general static asset (gallery images, OG images, logos).
 *
 * @param {string} path - e.g. 'images/og/home.webp' or 'logo/trenchmate-logo.svg'
 * @returns {string} Full CDN URL in production; local path in development
 */
function staticAsset(path) {
    return resolveAsset(path);
}

/**
 * Get the CDN URL for a document (PDF spec sheets).
 *
 * @param {string} path - e.g. 'product/trench-shield/trench-shield.pdf'
 * @returns {string} Full CDN URL in production; local path in development
 */
function documentAsset(path) {
    return resolveAsset(path);
}

/**
 * Get the extensionless base path for responsive srcSet generation.
 *
 * "product/trench-shield/trench-shield.webp" → "product/trench-shield/trench-shield"
 * "images/gallery-photo.jpg" → "images/gallery-photo"
 *
 * @param {string} path - Asset path (may include extension)
 * @returns {string} Path without file extension
 */
function responsiveAsset(path) {
    if (!path || typeof path !== 'string') return '';
    return path.replace(/\.[^/.]+$/, '');
}

module.exports = {
    productAsset,
    staticAsset,
    documentAsset,
    responsiveAsset,
};

try {
    Object.defineProperty(module.exports, '__esModule', { value: true });
    module.exports.default = productAsset;
} catch (_e) {
    // ignore
}
